import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  useTheme
} from '@mui/material';
import { holdBillService } from '../../services/holdBillService';

const HoldBillModal = ({ open, onClose, customer, cartItems, onSuccess }) => {
  const theme = useTheme();
  const [billName, setBillName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Reset form when dialog opens/closes
  const handleClose = () => {
    setBillName('');
    setError('');
    setLoading(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!billName.trim()) {
      setError('Bill name is required');
      return;
    }

    if (!customer || !customer.id) {
      setError('Customer is required');
      return;
    }

    if (!cartItems || cartItems.length === 0) {
      setError('Cart cannot be empty');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const holdBillData = {
        customerId: customer.id,
        billName: billName.trim(),
        customerName: customer.name,
        items: cartItems.map(item => {
          const unitPrice = item.price || item.product.sellingPrice || item.product.salePrice || 0;
          const taxRateObj = (item.tax || item.product?.tax);
          const taxRate = taxRateObj && typeof taxRateObj.rate === 'number'
            ? taxRateObj.rate
            : (taxRateObj && taxRateObj.rate != null
                ? Number(taxRateObj.rate)
                : 0);
          const taxAmount = unitPrice * item.quantity * (taxRate / 100);

          // Validate item data
          if (!item.product.id || !item.quantity || unitPrice <= 0) {
            throw new Error(`Invalid item data for product: ${item.product.name}`);
          }

          return {
            productId: item.product.id,
            productName: item.product.name,
            itemCode: item.itemCode || item.product?.sku || item.product?.barcode || null,
            taxRate: taxRate || null,
            quantity: item.quantity,
            unitPrice: unitPrice,
            taxAmount: taxAmount,
            tax: item.tax || item.product?.tax || null
          };
        })
      };

      console.log('Sending hold bill data:', holdBillData); // Debug log
      await holdBillService.createHoldBill(holdBillData);
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Hold bill error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to hold bill';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Hold Bill</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Customer: {customer?.name}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            Items in Cart: {cartItems.length}
          </Typography>
          <TextField
            fullWidth
            label="Bill Name"
            value={billName}
            onChange={(e) => setBillName(e.target.value)}
            error={!!error}
            helperText={error}
            sx={{ mt: 2 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? 'Holding...' : 'Hold Bill'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HoldBillModal; 